import { useTranslation } from 'react-i18next'
import type { SupportedLangCode } from '@/application/i18n/language'
import type { TaxonomyTerm } from '@/domains/types'
import { Input } from '@/ui/components/common/Input'

const MAX_LABEL_LENGTH = 100

interface TermLabelsEditorProps {
  terms: TaxonomyTerm[]
  /** The locale whose labels are edited. */
  locale: SupportedLangCode
  /** The investigation's source locale: the reference text shown next to each input. */
  referenceLocale: SupportedLangCode
  /** The labels edited so far, by term id (only the edited ones). */
  values: Record<string, string>
  onChange: (termId: string, label: string) => void
  disabled?: boolean
}

/**
 * One label input per linked term, for one locale. Nothing can be linked or
 * unlinked here: a translation tab only types the label of each term already
 * linked in the source tab. A term written in `locale` itself has no label
 * slot there (its name is that label), so it is shown read-only.
 */
export function TermLabelsEditor({
  terms,
  locale,
  referenceLocale,
  values,
  onChange,
  disabled,
}: TermLabelsEditorProps) {
  const { t } = useTranslation()

  if (terms.length === 0) {
    return <p className="text-sm text-muted-foreground italic">{t('translations.no_categories')}</p>
  }

  return (
    <div className="space-y-3">
      <ul className="space-y-3">
        {terms.map((term) => {
          if (term.sourceLocale === locale) {
            return (
              <li key={term.id} className="space-y-1">
                <p className="text-sm font-medium" lang={locale}>
                  {term.name}
                </p>
                <p className="text-xs text-muted-foreground">{t('taxonomy.canonical_name_hint')}</p>
              </li>
            )
          }

          const referenceLabel = term.labels[referenceLocale]
          const referenceText = referenceLabel ?? term.name
          const referenceLang = referenceLabel ? referenceLocale : term.sourceLocale
          return (
            <li key={term.id} className="space-y-1">
              <Input
                value={values[term.id] ?? term.labels[locale] ?? ''}
                onChange={(event) => onChange(term.id, event.target.value)}
                maxLength={MAX_LABEL_LENGTH}
                disabled={disabled}
                aria-label={t('taxonomy.label_input_aria', {
                  name: referenceText,
                  locale: locale.toUpperCase(),
                })}
              />
              <div className="text-sm text-muted-foreground border-l-2 border-border pl-2">
                <span className="font-medium">
                  {t('translations.source_field_label', { locale: referenceLang.toUpperCase() })}
                  {': '}
                </span>
                <span lang={referenceLang}>{referenceText}</span>
              </div>
            </li>
          )
        })}
      </ul>
      <p className="text-xs text-muted-foreground">{t('taxonomy.labels_shared_hint')}</p>
    </div>
  )
}
